import React from "react";
import { useSelector } from "react-redux";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import { SensitiveContentStatic } from "@gamedev-web/one-gaming-crm";
import {
  CheckBoxField,
  CheckBoxOptionEnabler,
  DropdownField,
  PricingPanel,
  RadioOptionWithDetails,
  SectionTitle,
  SeparatorBar,
  TextField,
} from "../../../components";
import { FormCard } from "../../../components/FormCard";
import { RightSideWrapper } from "../../../components/RightSideWrapper";
import { SubForm, SubFormNavigationProps } from "../../../components/SubForm";
import { storeContentPoliciesUrl } from "../../../constants";
import {
  gameInfoPricingAndAvailabilityInitialValues,
  useIdXboxGameInformationPricingAndAvailabilityValidationSchema,
} from "../../../helpers/formValidation";
import { useRatingsList } from "../../../helpers/optionSets";
import { useStrings } from "../../../localization/localizationHelpers";
import { getIsSavingForm } from "../../../redux/selectors";
import { GameInformationFormId, GameInformationSubFormsId } from "../GameInformationPageConstants";
import { useLanguagesList } from "./languagesList";
import * as styles from "./PricingAndAvailabilitySubForm.module.css";

const PricingAndAvailabilitySubForm = (props: SubFormNavigationProps) => {
  const [
    monetization,
    supportedLanguages,
    otherLanguages,
    anticipatedRatings,
    sensitiveContent,
    sensitiveContentSubtitle,
    sensitiveContentDetails,
    yes,
    no,
    maybe,
    other,
  ] = useStrings(
    "monetization",
    "supportedLanguages",
    "otherLanguages",
    "anticipatedRatings",
    "sensitiveContent",
    "sensitiveContentSubtitle",
    "sensitiveContentDetails",
    "yes",
    "no",
    "maybe",
    "other"
  );
  const languagesList = useLanguagesList();
  const validationSchema = useIdXboxGameInformationPricingAndAvailabilityValidationSchema();
  const isSavingFormToCrm = useSelector(getIsSavingForm);
  const ratingsList = useRatingsList();
  return (
    <SubForm
      validateConfig={validationSchema}
      initialValues={gameInfoPricingAndAvailabilityInitialValues}
      formId={GameInformationFormId}
      subFormId={GameInformationSubFormsId.PricingAndAvailability}
      {...props}
    >
      <SectionTitle title={supportedLanguages} />
      <RightSideWrapper
        rightSideComponent={<FormCard cardId={GameInformationSubFormsId.PricingAndAvailability} />}
      >
        <CheckBoxField
          checkBoxes={languagesList}
          name={"supportedLanguages"}
          isSaving={isSavingFormToCrm}
        />
      </RightSideWrapper>
      <CheckBoxOptionEnabler
        name={"hasOtherLanguages"}
        text={otherLanguages}
        isSaving={isSavingFormToCrm}
      >
        <TextField name={"otherLanguages"} isRequiredField={true} isSaving={isSavingFormToCrm} />
      </CheckBoxOptionEnabler>
      <SeparatorBar />
      <SectionTitle title={monetization} />
      <PricingPanel />
      <SeparatorBar />
      <SectionTitle title={anticipatedRatings} />
      <Row className={styles.container}>
        <Column>
          <DropdownField
            name="anticipatedRatings"
            values={ratingsList}
            title={anticipatedRatings}
            type="number"
            isSaving={isSavingFormToCrm}
          />
        </Column>
        <Column />
      </Row>
      <RadioOptionWithDetails
        name="sensitiveContent"
        title={sensitiveContent}
        subtitle={sensitiveContentSubtitle.replace("{0}", storeContentPoliciesUrl)}
        useFormattedSubtitle={true}
        isSaving={isSavingFormToCrm}
        detailsFieldName="sensitiveContentDetails"
        detailsTitle={sensitiveContentDetails}
        options={[
          {
            value: SensitiveContentStatic.Yes,
            label: yes,
          },
          {
            value: SensitiveContentStatic.No,
            label: no,
          },
          { value: SensitiveContentStatic.Maybe, label: maybe },
          { value: SensitiveContentStatic.Other, label: other },
        ]}
        detailsTriggerValuesArray={[
          SensitiveContentStatic.Yes,
          SensitiveContentStatic.Maybe,
          SensitiveContentStatic.Other,
        ]}
        isRequiredField={true}
      />
    </SubForm>
  );
};

export default PricingAndAvailabilitySubForm;
